import { getAuthToken } from "./api";

/**
 * Wrapper around fetch + ReadableStream for SSE. Parses named events from
 * `text/event-stream` and invokes a callback per event. Handles backpressure
 * naturally because we read the body directly.
 */
export type SseHandler = (event: string, data: unknown) => void;

/**
 * Scope pinned by the ContentPicker. Shipped as {@code context.primaryObject}
 * so the gateway's CognosContextPayload sees it — the same field the Cognos
 * plugin uses for its in-Cognos context.
 */
export type AskContext = {
  primaryObject?: {
    id?: string | null;
    type?: string;
    name?: string;
    path: string;
  };
};

export async function askStream(
  question: string,
  sessionId: string | undefined,
  onEvent: SseHandler,
  signal?: AbortSignal,
  context?: AskContext,
): Promise<void> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const body: Record<string, unknown> = { question, sessionId };
  if (context && context.primaryObject) body.context = context;
  const resp = await fetch("/api/v1/ask", {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal,
    credentials: "include",
  });
  if (!resp.ok || !resp.body) {
    throw new Error(`stream failed: ${resp.status}`);
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() ?? "";
    for (const block of parts) dispatch(block, onEvent);
  }
  if (buf.trim().length > 0) dispatch(buf, onEvent);
}

function dispatch(block: string, onEvent: SseHandler) {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
  }
  if (dataLines.length === 0) return;
  const data = dataLines.join("\n");
  try {
    onEvent(event, JSON.parse(data));
  } catch {
    onEvent(event, data);
  }
}
